import React, { ReactElement } from 'react';
import useStyles from './styles';
import { Control, DeepMap, FieldError } from 'react-hook-form';
import TranslatedText from '../../../../components/atoms/translated_text';
import CONFIGURATION, { Translations, CanModify } from './configuration';
import { Typography } from '@material-ui/core';
import {
  AppSelect,
  AppTextField,
  AppGroupText,
} from '../../../../components/molecules/form_controls';
import GridLayout from '../../../../components/molecules/grid_layout';
import { SchemeTypes } from '../../../../config';
import * as RegistrationDetailSelectors from 'redux/reducers/registration_detail_reducer/selectors';
import { FormFields } from '../../configuration';

interface IProps {
  mode: 'VIEW' | 'EDIT';
  scheme_type: SchemeTypes;
  is_agent: boolean;
  isExcluded: boolean;
  errors: DeepMap<FormFields, FieldError>;
  control: Control<FormFields>;
  getValues: any;
}

export default function ContactDetailsForm(props: IProps): ReactElement {
  // console.log('Render Contact Details Form');
  const classes = useStyles();

  const mode = props.mode;
  const SCHEME_TYPE = props.scheme_type;
  const IS_AGENT = props.is_agent;
  const errors = props.errors;
  const control = props.control;
  const isExcluded = props.isExcluded;

  return (
    <div>
      <Typography variant="h5" style={{ marginBottom: '16px' }}>
        <TranslatedText textMap={Translations.contact_details_header} />
      </Typography>
      <GridLayout
        elements={[
          {
            element: renderFirstNameField(
              errors,
              control,
              mode,
              SCHEME_TYPE,
              IS_AGENT,
              isExcluded
            ),
            size: 6,
            id: 'firstName',
          },
          {
            element: renderSurnameField(
              errors,
              control,
              mode,
              SCHEME_TYPE,
              IS_AGENT,
              isExcluded
            ),
            size: 6,
            id: 'surname',
          },
          {
            element: renderAddressField(
              errors,
              control,
              mode,
              SCHEME_TYPE,
              IS_AGENT,
              isExcluded
            ),
            size: 8,
            id: 'address',
          },
          {
            element: renderHouseNumberField(
              errors,
              control,
              mode,
              SCHEME_TYPE,
              IS_AGENT,
              isExcluded
            ),
            size: 2,
            id: 'number',
          },
          {
            element: renderPostalCodeField(
              errors,
              control,
              mode,
              SCHEME_TYPE,
              IS_AGENT,
              isExcluded
            ),
            size: 2,
            id: 'postalcode',
          },
          {
            element: renderCityField(
              errors,
              control,
              mode,
              SCHEME_TYPE,
              IS_AGENT,
              isExcluded
            ),
            size: 6,
            id: 'city',
          },
          {
            element: renderCountryField(
              errors,
              control,
              mode,
              SCHEME_TYPE,
              IS_AGENT,
              isExcluded
            ),
            size: 6,
            id: 'country',
          },
          {
            element: renderTelephoneField(
              errors,
              control,
              mode,
              SCHEME_TYPE,
              IS_AGENT,
              isExcluded
            ),
            size: 6,
            id: 'telephone',
          },
          {
            element: renderEmailField(
              errors,
              control,
              mode,
              SCHEME_TYPE,
              IS_AGENT,
              isExcluded
            ),
            size: 6,
            id: 'email',
          },
          {
            element: renderWebsitesField(
              errors,
              control,
              mode,
              SCHEME_TYPE,
              IS_AGENT,
              isExcluded,
              props.getValues
            ),
            size: 12,
            id: 'websites',
          },
        ]}
      />
    </div>
  );
}

const renderFirstNameField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean
) => {
  return (
    <AppTextField
      variant="outlined"
      // margin="normal"
      required
      fullWidth
      id="contactDetails_firstName"
      label={<TranslatedText textMap={Translations.first_name_label} />}
      error={'contactDetails_firstName' in errors}
      name="contactDetails_firstName"
      control={control}
      rules={{ required: 'Required Field' }}
      helperText={
        errors.contactDetails_firstName
          ? errors.contactDetails_firstName.message
          : ' '
      }
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes(
          'contactDetails_firstName'
        ) ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};

const renderSurnameField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean
) => {
  return (
    <AppTextField
      variant="outlined"
      // margin="normal"
      required
      fullWidth
      id="contactDetails_surname"
      label={<TranslatedText textMap={Translations.surname_label} />}
      error={'contactDetails_surname' in errors}
      name="contactDetails_surname"
      control={control}
      rules={{ required: 'Required Field' }}
      helperText={
        errors.contactDetails_surname
          ? errors.contactDetails_surname.message
          : ' '
      }
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes('contactDetails_surname') ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};

const renderAddressField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean
) => {
  return (
    <AppTextField
      variant="outlined"
      // margin="normal"
      required
      fullWidth
      id="contactDetails_address"
      label={<TranslatedText textMap={Translations.address_street_label} />}
      error={'contactDetails_address' in errors}
      name="contactDetails_address"
      control={control}
      rules={{ required: 'Required Field' }}
      helperText={
        errors.contactDetails_address
          ? errors.contactDetails_address.message
          : ' '
      }
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes('contactDetails_address') ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};

const renderHouseNumberField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean
) => {
  return (
    <AppTextField
      variant="outlined"
      // margin="normal"
      required
      fullWidth
      id="contactDetails_number"
      label={<TranslatedText textMap={Translations.number_label} />}
      error={'contactDetails_number' in errors}
      name="contactDetails_number"
      control={control}
      rules={{ required: 'Required Field' }}
      helperText={
        errors.contactDetails_number
          ? errors.contactDetails_number.message
          : ' '
      }
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes('contactDetails_number') ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};

const renderPostalCodeField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean
) => {
  return (
    <AppTextField
      variant="outlined"
      // margin="normal"
      required
      fullWidth
      id="contactDetails_postalcode"
      label={<TranslatedText textMap={Translations.postal_code_label} />}
      error={'contactDetails_postalcode' in errors}
      name="contactDetails_postalcode"
      control={control}
      rules={{ required: 'Required Field' }}
      helperText={
        errors.contactDetails_postalcode
          ? errors.contactDetails_postalcode.message
          : ' '
      }
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes(
          'contactDetails_postalcode'
        ) ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};

const renderCityField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean
) => {
  return (
    <AppTextField
      variant="outlined"
      // margin="normal"
      fullWidth
      label={<TranslatedText textMap={Translations.city_label} />}
      id="contactDetails_city"
      name="contactDetails_city"
      required
      error={'contactDetails_city' in errors}
      control={control}
      rules={{ required: 'Required Field' }}
      helperText={
        errors.contactDetails_city ? errors.contactDetails_city.message : ' '
      }
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes('contactDetails_city') ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};

const renderCountryField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean
) => {
  // let countries = getState(ReducerKeys.REGISTRATION_DETAIL_REDUCER)
  //   .allCountriesList.data;
  let countries = RegistrationDetailSelectors.useGetAllCountries();
  let select_countries = [];

  select_countries.push({
    id: null,
    label: Translations.country_label.English,
  });

  if (countries && countries.length > 0) {
    for (let country of countries) {
      if (country.code !== 'XI') {
        select_countries.push({
          id: country.country_id,
          label: country.description,
        });
      }
    }
  }

  return (
    <AppSelect
      variant="outlined"
      // margin="normal"
      fullWidth
      label={<TranslatedText textMap={Translations.country_label} />}
      id="contactDetails_country"
      name="contactDetails_country"
      required
      error={'contactDetails_country' in errors}
      control={control}
      rules={{ required: 'Required Field' }}
      options={select_countries as any}
      helperText={
        errors.contactDetails_country
          ? errors.contactDetails_country.message
          : ' '
      }
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes('contactDetails_country') ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};

const renderTelephoneField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean
) => {
  const phonePattern = /^(\+)?[0-9 \-]{0,20}$/;
  return (
    <AppTextField
      variant="outlined"
      // margin="normal"
      fullWidth
      label={<TranslatedText textMap={Translations.telephone_label} />}
      id="contactDetails_telephone"
      name="contactDetails_telephone"
      required
      error={'contactDetails_telephone' in errors}
      control={control}
      rules={{
        required: 'Required Field',
        pattern: {
          value: phonePattern,
          message: Translations.invalid_phone_format.English,
        },
      }}
      helperText={
        errors.contactDetails_telephone
          ? errors.contactDetails_telephone.message
          : ' '
      }
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes(
          'contactDetails_telephone'
        ) ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};

const renderEmailField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean
) => {
  return (
    <AppTextField
      variant="outlined"
      // margin="normal"
      fullWidth
      label="Email"
      id="contactDetails_email"
      name="contactDetails_email"
      required
      error={'contactDetails_email' in errors}
      control={control}
      type="email"
      rules={{ required: 'Required Field' }}
      helperText={
        errors.contactDetails_email ? errors.contactDetails_email.message : ' '
      }
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes('contactDetails_email') ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};

const renderWebsitesField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean,
  getValues: any
) => {
  function invalidWebsiteList(value: any) {
    if (SCHEME_TYPE === SchemeTypes.EU) {
      return false;
    }
    if (value && value.length > 0) {
      return false;
    }
    return true;
  }

  function invalidWebsiteLength(value: any) {
    for (let i = 0; i < value.length; i++) {
      if (value[i].length > CONFIGURATION.MAX_WEBSITE_LENGTH) {
        return true;
      }
    }
    return false;
  }

  return (
    <AppGroupText
      variant="outlined"
      // margin="normal"
      required
      fullWidth
      id="contactDetails_websites"
      label={<TranslatedText textMap={Translations.website_label} />}
      error={'contactDetails_websites' in errors}
      name="contactDetails_websites"
      control={control}
      rules={{
        validate: () =>
          invalidWebsiteList(getValues('contactDetails_websites')) ? (
            <TranslatedText textMap={Translations.form_error_required} />
          ) : invalidWebsiteLength(getValues('contactDetails_websites')) ? (
            <TranslatedText textMap={Translations.invalid_website_length} />
          ) : null,
      }}
      helperText={
        errors.contactDetails_websites ? (
          errors.contactDetails_websites.message
        ) : (
          <TranslatedText textMap={Translations.website_help} />
        )
      }
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes('contactDetails_websites') ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};
